import { ApolloError, DocumentNode, useMutation } from '@apollo/client';
import { StackShim as Stack } from '@fluentui/react-migration-v8-v9';
import {
  MessageBar,
  MessageBarBody,
  Button,
  Persona,
  PresenceBadgeStatus,
  Spinner,
  Tooltip,
  makeStyles,
} from '@fluentui/react-components';
import React from 'react';
import { IReview, IReviewApproval } from '../../utils/types/IReview';
import CanAccess from '../common/canAccess/CanAccess';
import LoadingErrorMessage from '../common/errorContent/LoadingErrorMessage';
import RejectButton from './RejectButton';
import RejectButtonMode from './RejectButtonMode';
import { IReviewActionButton } from './IReviewActionButton';
import ReviewStatus from './ReviewStatus';

interface IReviewApprovalsProps {
  review: IReview;
  reviewApprovals: Array<IReviewApproval>;
  approveMutation?: DocumentNode;
  rejectMutation?: DocumentNode;
  loading: boolean;
  rejectButtonMode?: RejectButtonMode;
  disableApproveButton?: boolean;
  approverWarningMessage?: string;
  displayOnly?: boolean;
  customActionButtons?: IReviewActionButton[];
  onApprovalOverrideMethod?: () => void;
  onRejectOverrideMethod?: () => void;
}

interface IReviewApprovalsViewProps
  extends Omit<IReviewApprovalsProps, 'approveMutation' | 'loading'> {
  approveLoading: boolean;
  approveError?: ApolloError;
  rejectLoading: boolean;
  rejectError?: ApolloError;
  onApproveReview?: (approval: IReviewApproval) => void;
  onRejectReview?: (approval: IReviewApproval, reason?: string, callback?: () => void) => void;
}

interface IMutationReviewApprovalsProps
  extends Omit<IReviewApprovalsProps, 'approveMutation' | 'loading'> {
  approveMutation: DocumentNode;
}

const useStyles = makeStyles({
  approvalPersona: {
    flexBasis: '175px',
    width: '250px',
  },
});

const renderSecondaryText = (approval: IReviewApproval): string => {
  let secondaryText = 'Pending';

  if (approval.status.id === ReviewStatus.Accepted) {
    secondaryText = `Approved via ${approval.approver?.displayName ?? 'Unknown'}`;
  } else if (approval.status.id === ReviewStatus.Rejected) {
    secondaryText = `Rejected via ${approval.approver?.displayName ?? 'Unknown'}`;
  } else if (approval.status.id === ReviewStatus.ChangesRequested) {
    secondaryText = `Changes Requested via ${approval.approver?.displayName ?? 'Unknown'}`;
  }

  return secondaryText;
};

const ReviewApprovalsView = ({
  review,
  reviewApprovals,
  rejectMutation,
  rejectButtonMode = RejectButtonMode.Reject,
  disableApproveButton,
  approverWarningMessage,
  displayOnly,
  customActionButtons,
  onRejectOverrideMethod,
  approveLoading,
  approveError,
  rejectLoading,
  rejectError,
  onApproveReview,
  onRejectReview,
}: IReviewApprovalsViewProps): JSX.Element => {
  const styles = useStyles();
  const isReviewEditable =
    !approveLoading &&
    !rejectLoading &&
    (review?.status === 'Pending' || review?.status === 'Changes Requested');

  return (
    <>
      <Stack horizontal horizontalAlign="space-between">
        <h3>Approvers</h3>
        {(approveLoading || rejectLoading) && <Spinner aria-label="Loading" />}
      </Stack>
      <LoadingErrorMessage
        loading={approveLoading}
        error={approveError}
        actionName="approving this review"
      />
      {rejectMutation && (
        <LoadingErrorMessage
          loading={rejectLoading}
          error={rejectError}
          actionName="rejecting this review"
        />
      )}
      <Stack tokens={{ childrenGap: 30 }}>
        {approverWarningMessage && (
          <MessageBar intent="info" layout="multiline">
            <MessageBarBody>{approverWarningMessage}</MessageBarBody>
          </MessageBar>
        )}
        {reviewApprovals.map((approval) => {
          const secondaryText = renderSecondaryText(approval);
          let approvedStyle: PresenceBadgeStatus | undefined;
          if (approval.status.id === ReviewStatus.Accepted) {
            approvedStyle = 'available';
          } else if (approval.status.id === ReviewStatus.Rejected) {
            approvedStyle = 'blocked';
          } else if (approval.status.id === ReviewStatus.ChangesRequested) {
            approvedStyle = 'away';
          }

          return (
            <Stack
              key={approval.id}
              tokens={{ childrenGap: 10 }}
              horizontal
              horizontalAlign="space-between"
            >
              <Persona
                name={approval.approvalGroupName}
                secondaryText={secondaryText}
                avatar={{
                  size: 56,
                  color: 'colorful',
                  badge: approvedStyle ? { status: approvedStyle } : undefined,
                }}
                size="large"
                textAlignment="center"
                className={styles.approvalPersona}
              />
              <Stack horizontal tokens={{ childrenGap: 10 }} verticalAlign="center">
                {customActionButtons && customActionButtons.length > 0 && (
                  <Stack tokens={{ childrenGap: 8 }} style={{ minWidth: '130px' }}>
                    {customActionButtons.map((btn) => (
                      <CanAccess key={btn.label} requestedAction={btn.accessPolicy}>
                        {btn.tooltip ? (
                          <Tooltip content={btn.tooltip} relationship="description" withArrow>
                            <span style={{ display: 'inline-block', width: '100%' }}>
                              <Button
                                appearance={btn.appearance}
                                disabled={btn.disabled}
                                onClick={btn.onClick}
                                style={{
                                  width: '100%',
                                  pointerEvents: btn.disabled ? 'none' : undefined,
                                }}
                              >
                                {btn.label}
                              </Button>
                            </span>
                          </Tooltip>
                        ) : (
                          <Button
                            appearance={btn.appearance}
                            disabled={btn.disabled}
                            onClick={btn.onClick}
                            style={{ width: '100%' }}
                          >
                            {btn.label}
                          </Button>
                        )}
                      </CanAccess>
                    ))}
                  </Stack>
                )}
                {!displayOnly && !customActionButtons?.length && (
                  <CanAccess requestedAction={approval.approvalEntityAction}>
                    <Button
                      appearance="primary"
                      disabled={
                        !isReviewEditable ||
                        approval.status.id === ReviewStatus.Accepted ||
                        disableApproveButton
                      }
                      onClick={(): void => onApproveReview?.(approval)}
                    >
                      Approve
                    </Button>
                  </CanAccess>
                )}
                {rejectMutation && !displayOnly && !customActionButtons?.length && (
                  <CanAccess requestedAction={approval.approvalEntityAction}>
                    <RejectButton
                      disabled={!isReviewEditable || approval.status.id === ReviewStatus.Rejected}
                      onClick={(reason?: string, callback?: () => void): void => {
                        onRejectReview?.(approval, reason, callback);
                      }}
                      mode={rejectButtonMode}
                      mutationLoading={rejectLoading}
                      mutationError={rejectError}
                      onRejectOverrideMethod={onRejectOverrideMethod}
                    />
                  </CanAccess>
                )}
              </Stack>
            </Stack>
          );
        })}
      </Stack>
    </>
  );
};

const MutationReviewApprovals = ({
  review,
  reviewApprovals,
  approveMutation,
  rejectMutation,
  onApprovalOverrideMethod,
  onRejectOverrideMethod,
  ...viewProps
}: IMutationReviewApprovalsProps): JSX.Element => {
  const [approve, { loading: approveLoading, error: approveError }] = useMutation(approveMutation);
  const [reject, { loading: rejectLoading, error: rejectError }] = useMutation(
    rejectMutation ?? approveMutation,
  );

  const onApproveReview = (approval: IReviewApproval): void => {
    if (onApprovalOverrideMethod) {
      onApprovalOverrideMethod();
      return;
    }

    approve({
      variables: {
        reviewId: review.id,
        approvalId: approval.id,
      },
    });
  };

  const onRejectReview = (
    approval: IReviewApproval,
    reason?: string,
    callback?: () => void,
  ): void => {
    if (onRejectOverrideMethod) {
      onRejectOverrideMethod();
      return;
    }

    reject({
      variables: {
        reviewId: review.id,
        approvalId: approval.id,
        reason,
      },
    }).then(() => {
      callback?.();
    });
  };

  return (
    <ReviewApprovalsView
      {...viewProps}
      review={review}
      reviewApprovals={reviewApprovals}
      rejectMutation={rejectMutation}
      onRejectOverrideMethod={onRejectOverrideMethod}
      approveLoading={approveLoading}
      approveError={approveError}
      rejectLoading={rejectLoading}
      rejectError={rejectError}
      onApproveReview={onApproveReview}
      onRejectReview={onRejectReview}
    />
  );
};

const ReviewApprovals = ({
  review,
  reviewApprovals,
  approveMutation,
  rejectMutation,
  loading,
  customActionButtons,
  ...props
}: IReviewApprovalsProps): JSX.Element => {
  if (!reviewApprovals) {
    return <div>No approvers have been set.</div>;
  }

  if (loading) {
    return <Spinner aria-label="Loading" />;
  }

  if (customActionButtons?.length) {
    return (
      <ReviewApprovalsView
        {...props}
        review={review}
        reviewApprovals={reviewApprovals}
        rejectMutation={rejectMutation}
        customActionButtons={customActionButtons}
        approveLoading={false}
        rejectLoading={false}
      />
    );
  }

  if (!approveMutation) {
    throw new Error('approveMutation is required when custom action buttons are not provided.');
  }

  return (
    <MutationReviewApprovals
      {...props}
      review={review}
      reviewApprovals={reviewApprovals}
      approveMutation={approveMutation}
      rejectMutation={rejectMutation}
    />
  );
};

export default ReviewApprovals;
